import { Drawer, styled, Typography } from '@mui/material';
import { type ReactNode, useId } from 'react';

import { CloseIcon } from '../../../icons';
import { IconButton } from '../../atoms/IconButton';

type Props = {
  open: boolean;
  onClose: () => void;
  closeLabel: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Pinned under the scrollable content (e.g. "Voir plus"). */
  footer?: ReactNode;
  'data-testid'?: string;
};

const PANEL_WIDTH = 480;

const Panel = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100vw',
  height: '100%',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up('sm')]: {
    width: PANEL_WIDTH,
  },
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  paddingTop: `max(${theme.spacing(1.5)}, env(safe-area-inset-top))`,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderText = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Content = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: theme.spacing(2),
}));

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(1, 2),
  paddingBottom: `max(${theme.spacing(1)}, env(safe-area-inset-bottom))`,
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

/** Panel sliding in from the right to consult a secondary list without leaving the page; full screen on phones. */
export function SidePanel(props: Props) {
  const titleId = useId();

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose} aria-labelledby={titleId}>
      <Panel data-testid={props['data-testid'] ?? 'side-panel'}>
        <Header>
          <HeaderText>
            <Typography id={titleId} variant="h4" component="h2">
              {props.title}
            </Typography>
            {props.subtitle ? (
              <Typography variant="body2" color="text.secondary">
                {props.subtitle}
              </Typography>
            ) : null}
          </HeaderText>
          <IconButton tooltip={props.closeLabel} edge="end" onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>{props.children}</Content>
        {props.footer ? <Footer>{props.footer}</Footer> : null}
      </Panel>
    </Drawer>
  );
}

export type { Props as SidePanelProps };
